import { useCallback, useEffect, useRef, useState } from "react";

export const POLL_MS = 15_000;

export type PolledResource<T> = {
  /** Senast lyckade svar. Ligger kvar när en senare hämtning misslyckas. */
  data: T | null;
  /** Satt när senaste hämtningen misslyckades, tom efter nästa lyckade. */
  error: unknown;
  /** Sant tills första svaret kommit, och under en manuell omladdning utan data. */
  isLoading: boolean;
  /** Sant när något svar har kommit minst en gång för den här nyckeln. */
  hasLoaded: boolean;
  /** Hämta direkt, utan att vänta på nästa poll. */
  refresh: () => void;
};

/**
 * Hämtar något, och hämtar det igen med jämna mellanrum.
 *
 * Det viktiga är vad som händer när en poll misslyckas: det tidigare svaret
 * behålls. En tappad anslutning ska göra att vyn säger att den visar det
 * senast kända, inte att den tömmer sig själv. `error` sätts bredvid datat
 * i stället för att ersätta det, och det är vyn som avgör vad den säger.
 *
 * Byter `key` (ett annat ställe) glöms allt, och ett svar som landar efter
 * bytet skrivs aldrig in.
 */
export function usePolledResource<T>(
  load: () => Promise<T>,
  { key, enabled = true, intervalMs = POLL_MS }: { key: string; enabled?: boolean; intervalMs?: number }
): PolledResource<T> {
  const [data, setData] = useState<T | null>(null);
  const [error, setError] = useState<unknown>(null);
  const [hasLoaded, setHasLoaded] = useState(false);
  const [isLoading, setIsLoading] = useState(enabled);

  // Läses vid anrop, så en ny funktionsidentitet inte startar om pollen.
  const loadRef = useRef(load);
  loadRef.current = load;

  // Ökar vid varje byte av nyckel; ett svar från en tidigare generation
  // skrivs aldrig in.
  const generation = useRef(0);
  const runRef = useRef<() => Promise<void>>(async () => {});

  useEffect(() => {
    generation.current += 1;
    const mine = generation.current;

    setData(null);
    setError(null);
    setHasLoaded(false);

    if (!enabled) {
      setIsLoading(false);
      runRef.current = async () => {};
      return;
    }

    setIsLoading(true);

    const run = async () => {
      try {
        const result = await loadRef.current();
        if (generation.current !== mine) return;

        setData(result);
        setError(null);
        setHasLoaded(true);
      } catch (e) {
        if (generation.current !== mine) return;

        // Behåll tidigare data — en tappad poll ska inte tömma vyn.
        setError(e);
      } finally {
        if (generation.current === mine) setIsLoading(false);
      }
    };

    runRef.current = run;
    void run();

    const interval = window.setInterval(() => void run(), intervalMs);

    return () => {
      generation.current += 1;
      window.clearInterval(interval);
    };
  }, [key, enabled, intervalMs]);

  const refresh = useCallback(() => {
    setIsLoading(true);
    void runRef.current();
  }, []);

  return { data, error, isLoading, hasLoaded, refresh };
}
